import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Table, Card, Badge, Row, Col, Nav } from 'react-bootstrap';
import { Bar, Pie } from 'react-chartjs-2';
import Navbar from './Navbar';
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';

const CHART_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#6366F1'
];

const RATING_LABELS = {
  0: 'Unsatisfactory',
  1: 'Needs Improvement',
  2: 'Meets Expectations',
  3: 'Exceeds Expectations',
  4: 'Outstanding'
};

const getQuestionTypeLabel = (type) => {
  const labels = {
    text: 'SHORT ANSWER',
    textarea: 'LONG ANSWER',
    radio: 'SINGLE CHOICE',
    checkbox: 'MULTIPLE CHOICE',
    rating: 'RATING SCALE',
    'radio-matrix': 'RATING MATRIX',
    dropdown: 'DROPDOWN',
    multiple_choice: 'MULTIPLE CHOICE'
  };
  return labels[type] || 'QUESTION';
};

const donutChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '62%',
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label(context) {
          const value = context.raw;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
          return `${context.label}: ${value} (${percentage}%)`;
        }
      }
    }
  }
};

const horizontalBarOptions = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label(context) {
          const value = context.raw;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
          return `${context.label}: ${value} (${percentage}%)`;
        }
      }
    }
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: '#f3f4f6' },
      ticks: { stepSize: 1, color: '#9ca3af', font: { size: 11 } }
    },
    y: {
      grid: { display: false },
      ticks: { color: '#374151', font: { size: 12 } }
    }
  }
};

const ChartLegend = ({ labels }) => (
  <div className="survey-results-legend">
    {labels.map((label, i) => (
      <div key={i} className="survey-results-legend-item">
        <span
          className="survey-results-legend-dot"
          style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
        />
        <span>{label}</span>
      </div>
    ))}
  </div>
);

const truncateLabel = (text, maxLen = 14) =>
  (text.length > maxLen ? `${text.slice(0, maxLen)}…` : text);

const getMatrixColSize = (count) => 12 / Math.min(Math.max(count, 1), 4);

const BreakdownTable = ({ labels, data, labelFormatter, showBars = true, compact = false }) => {
  const total = data.reduce((a, b) => a + b, 0);
  const table = (
    <Table
      responsive={false}
      className={`survey-results-table smaller mb-0${compact ? ' survey-results-matrix-table' : ''}`}
    >
      <colgroup>
        <col className="survey-results-col-option" />
        <col className="survey-results-col-count" />
        <col className="survey-results-col-pct" />
      </colgroup>
      <thead>
        <tr>
          <th>Option</th>
          <th>Count</th>
          <th>%</th>
        </tr>
      </thead>
      <tbody>
        {labels.map((label, idx) => {
          const pct = total > 0 ? (data[idx] / total) * 100 : 0;
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          return (
            <tr key={idx}>
              <td>
                <div className="survey-results-table-option">
                  <span
                    className="survey-results-legend-dot"
                    style={{ backgroundColor: color }}
                  />
                  <span className="survey-results-table-label">
                    {labelFormatter ? labelFormatter(label) : label}
                  </span>
                  {showBars && (
                    <div className="survey-results-progress-track">
                      <div
                        className="survey-results-progress-fill"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                  )}
                </div>
              </td>
              <td>{data[idx]}</td>
              <td>{pct.toFixed(1)}%</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  return (
    <div className={`survey-results-table-wrap${compact ? ' survey-results-matrix-table-wrap' : ''}`}>
      {table}
    </div>
  );
};

const MetricCard = ({ icon, iconClass, label, value, subtext }) => (
  <Card className="border-0 shadow-sm">
    <Card.Header className="p-2 px-3 border-0 d-flex justify-content-between align-items-center">
        <div className="text-uppercase smaller text-muted">{label}</div>
        <div className={`survey-results-metric-icon survey-results-metric-icon--${iconClass}`}>
          <i className={`bi ${icon}`} />
        </div>
        </Card.Header>
    <Card.Body>
      <h1 className="fw-bold mb-0">{value}</h1>
      <div className="text-muted small mb-0">{subtext}</div>
    </Card.Body>
  </Card>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-5">
    <i className="bi bi-chat-dots d-block mb-2" style={{ fontSize: 28, opacity: 0.4 }} />
    <small className="text-muted">{message}</small>
  </div>
);

const getAssessmentText = (metrics, averageScore) => {
  const score = parseFloat(averageScore);
  if (score >= 4) return 'Strong performance across all dimensions';
  if (score >= 3) return 'Solid results with room for improvement';
  if (score >= 2) return 'Mixed results — review responses for details';
  return 'Limited data available — more responses needed';
};

const SurveyResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [template, setTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const templateData = localStorage.getItem('selectedTemplate');
        if (!templateData) {
          throw new Error('Template data not found');
        }
        const parsedTemplate = JSON.parse(templateData);

        const token = localStorage.getItem('token');
        const response = await fetch(`${API_ENDPOINTS.DIRECTOR}/survey-results/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch responses');
        }

        const data = await response.json();

        const mergedTemplate = {
          ...parsedTemplate,
          questions: parsedTemplate.questions || [],
          responses: data.responses || [],
          totalResponses: data.totalResponses || 0,
          totalAssigned: data.totalAssigned || 0,
          completedResponses: data.completedResponses || 0,
          averageScore: data.averageScore || 0
        };

        if (mergedTemplate.questions && data.questions) {
          mergedTemplate.questions = mergedTemplate.questions.map(templateQuestion => {
            const responseQuestion = data.questions.find(q => q.id === templateQuestion.id);
            if (responseQuestion) {
              return {
                ...templateQuestion,
                responses: responseQuestion.responses || []
              };
            }
            return templateQuestion;
          });
        }

        setTemplate(mergedTemplate);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching survey results:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const prepareChartData = (question, factor = null) => {
    if (!question.responses || !question.responses.length) return null;

    const answerCounts = {};
    question.responses.forEach(response => {
      const answer = factor ? response.answer[factor] : response.answer;
      if (answer) {
        if (Array.isArray(answer)) {
          answer.forEach(a => {
            answerCounts[a] = (answerCounts[a] || 0) + 1;
          });
        } else {
          answerCounts[answer] = (answerCounts[answer] || 0) + 1;
        }
      }
    });

    const labels = Object.keys(answerCounts);
    const data = Object.values(answerCounts);

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
        borderWidth: 0
      }]
    };
  };

  const getMatrixFactors = (question) => {
    let allFactors = [];
    if (question.options?.length) allFactors = Array.from(new Set(question.options));
    else if (question.factors?.length) allFactors = Array.from(new Set(question.factors));
    else if (question.choices?.length) allFactors = Array.from(new Set(question.choices));
    else if (question.matrixOptions?.length) allFactors = Array.from(new Set(question.matrixOptions));

    if (allFactors.length === 0 && question.questionText?.includes('rate this form based on how it functions well')) {
      allFactors = ['Design and Consistency', 'Functionality', 'Usefulness'];
    }
    return allFactors;
  };

  const getMatrixFactorName = (allFactors, item, index) => {
    if (allFactors.length > 0 && allFactors[index]) return allFactors[index];
    const optionKey = parseInt(item.option);
    if (!isNaN(optionKey)) {
      const defaultNames = ['Design and Consistency', 'Functionality', 'Usefulness'];
      return defaultNames[optionKey] || `Factor ${optionKey + 1}`;
    }
    return item.option;
  };

  const renderDonutWithTable = (chartData, labelFormatter) => (
    <>
      <div className="survey-results-chart-wrap">
        <Pie data={chartData} options={donutChartOptions} />
      </div>
      <ChartLegend labels={chartData.labels} />
      <div className="mt-3">
        <BreakdownTable
          labels={chartData.labels}
          data={chartData.datasets[0].data}
          labelFormatter={labelFormatter}
        />
      </div>
    </>
  );

  const renderHorizontalBarWithTable = (chartData) => (
    <>
      <div style={{ height: Math.max(100, chartData.labels.length * 44) }}>
        <Bar
          data={{
            labels: chartData.labels,
            datasets: [{
              data: chartData.datasets[0].data,
              backgroundColor: chartData.labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
              borderRadius: 6,
              borderSkipped: false
            }]
          }}
          options={horizontalBarOptions}
        />
      </div>
      <div className="mt-3">
        <BreakdownTable labels={chartData.labels} data={chartData.datasets[0].data} />
      </div>
    </>
  );

  const renderQuestionResults = (question) => {
    if (question.questionType === 'header-paragraph') {
      try {
        const headerData = JSON.parse(question.questionText);
        return (
          <div>
            <h5 className={`mb-2 ${headerData.size === 'h1' ? 'h4' : headerData.size === 'h3' ? 'h6' : 'h5'}`}>
              {headerData.header}
            </h5>
            <p className="survey-results-description-text">{headerData.paragraph}</p>
          </div>
        );
      } catch {
        return <p className="survey-results-description-text mb-0">{question.questionText}</p>;
      }
    }

    if (!question.responses || question.responses.length === 0) {
      return <EmptyState message="No responses yet for this question" />;
    }

    if (question.questionType === 'text' || question.questionType === 'textarea') {
      const count = question.responses.length;
      return (
        <div>
          <div className="text-muted text-uppercase smaller mb-2">
            Recent Responses <Badge className="bg-secondary small ms-1 p-1 px-2">{count}</Badge>
          </div>
          {question.responses.slice(0, 5).map((response, idx) => (
            <div key={idx} className="p-3 card-bg-container rounded bg-light small">
              <i className="bi bi-quote me-2 text-muted"></i>
              {response.answer}
            </div>
          ))}
        </div>
      );
    }

    if (question.questionType === 'radio-matrix') {
      const allFactors = getMatrixFactors(question);
      const responseCounts = {};

      question.responses.forEach(response => {
        if (!response?.answer) return;
        Object.entries(response.answer).forEach(([option, value]) => {
          if (!responseCounts[option]) responseCounts[option] = {};
          responseCounts[option][value] = (responseCounts[option][value] || 0) + 1;
        });
      });

      const chartData = Object.entries(responseCounts).map(([option, counts]) => ({
        option,
        ...counts
      }));

      if (chartData.length === 0) {
        return <EmptyState message="No valid responses for this matrix question" />;
      }

      const allValues = [...new Set(question.responses.flatMap(r =>
        r.answer ? Object.values(r.answer) : []
      ))].sort();

      const colSize = getMatrixColSize(chartData.length);

      return (
        <Row className="survey-results-matrix-factors g-3">
          {chartData.map((item, index) => {
            const factorName = getMatrixFactorName(allFactors, item, index);
            const factorData = allValues.map(value => ({
              level: RATING_LABELS[value] || `Rating ${value}`,
              count: item[value] || 0
            }));

            return (
              <Col key={index} xs={12} md={colSize} className="survey-results-matrix-col">
                <div className="survey-results-matrix-factor h-100">
                <div className="survey-results-matrix-factor-title">{factorName}</div>
                <div
                  className="survey-results-matrix-chart"
                  style={{ height: Math.max(100, factorData.length * 30) }}
                >
                  <Bar
                    data={{
                      labels: factorData.map(d => truncateLabel(d.level)),
                      datasets: [{
                        data: factorData.map(d => d.count),
                        backgroundColor: factorData.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                        borderRadius: 4,
                        borderSkipped: false,
                        barThickness: 14
                      }]
                    }}
                    options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      layout: { padding: { right: 4 } },
                      scales: {
                        x: {
                          beginAtZero: true,
                          grid: { color: '#f3f4f6' },
                          ticks: { stepSize: 1, color: '#9ca3af', font: { size: 9 }, maxTicksLimit: 5 }
                        },
                        y: {
                          grid: { display: false },
                          ticks: {
                            color: '#374151',
                            font: { size: 9 },
                            autoSkip: false,
                            maxRotation: 0,
                            minRotation: 0
                          }
                        }
                      }
                    }}
                  />
                </div>
                <BreakdownTable
                  labels={factorData.map(d => d.level)}
                  data={factorData.map(d => d.count)}
                  showBars={false}
                  compact
                />
                </div>
              </Col>
            );
          })}
        </Row>
      );
    }

    const chartData = prepareChartData(question);
    if (!chartData) return <EmptyState message="No responses yet for this question" />;

    if (question.questionType === 'rating') {
      const scalePairs = chartData.labels
        .map((label, i) => ({ label, count: chartData.datasets[0].data[i] }))
        .sort((a, b) => Number(a.label) - Number(b.label));
      const scaleLabels = scalePairs.map(p => p.label);
      const scaleData = scalePairs.map(p => p.count);

      return (
        <>
          <div className="survey-results-rating-scale">
            {scaleLabels.map((label, i) => (
              <div
                key={label}
                className="survey-results-rating-box"
                style={{ borderColor: CHART_COLORS[i % CHART_COLORS.length] }}
              >
                {label}
              </div>
            ))}
          </div>
          {renderHorizontalBarWithTable({ labels: scaleLabels, datasets: [{ data: scaleData }] })}
        </>
      );
    }

    if (question.questionType === 'radio') {
      return renderHorizontalBarWithTable(chartData);
    }

    return renderDonutWithTable(chartData);
  };

  const renderQuestionCards = () => (
    <div className="row g-4">
      {template.questions?.map((question, index) => {
        if (question.questionType === 'header-paragraph') return null;

        const questionNumber = template.questions
          .slice(0, index + 1)
          .filter(q => q.questionType !== 'header-paragraph')
          .length;

        const isFullWidth = question.questionType === 'radio-matrix';

        return (
          <div className={isFullWidth ? 'col-12 survey-results-matrix-col' : 'col-lg-6'} key={question.id || index}>
            <Card className={`h-100 border p-2${isFullWidth ? ' survey-results-matrix-card' : ''}`}>
              <Card.Header className="p-2 px-3 border-0 d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-center gap-2">
                    <div className="survey-results-question-number">{questionNumber}</div>
                    <p className="fw-semibold small mb-0">{question.questionText}</p>
                  </div>
              </Card.Header>
              <Card.Body>
                {renderQuestionResults(question)}
              </Card.Body>
            </Card>
          </div>
     
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <div className="survey-results-page min-vh-100">
        <PageTitle title="Loading..." />
        <Navbar />
        <Container className="py-5">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading survey results...</span>
              </div>
              <p className="mt-3 text-muted">Loading survey results...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-results-page min-vh-100">
        <PageTitle title="Survey Error" />
        <Navbar />
        <Container className="py-4">
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        </Container>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="survey-results-page min-vh-100">
        <PageTitle title="No Template Data" />
        <Navbar />
        <Container className="py-4">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            No template data available
          </div>
          <Button variant="primary" onClick={() => navigate('/evaluations')}>
            Back to Dashboard
          </Button>
        </Container>
      </div>
    );
  }

  const calculateMetrics = (tpl) => {
    const completionRate = tpl.totalAssigned
      ? (tpl.completedResponses / tpl.totalAssigned) * 100
      : 0;
    const responseRate = tpl.totalAssigned
      ? (tpl.totalResponses / tpl.totalAssigned) * 100
      : 0;

    return {
      completionRate: completionRate.toFixed(1),
      averageScore: tpl.averageScore ? tpl.averageScore.toFixed(1) : '0.0',
      responseRate: responseRate.toFixed(1)
    };
  };

  const metrics = calculateMetrics(template);
  const questionCount = template.questions?.filter(q => q.questionType !== 'header-paragraph').length || 0;
  const answeredCount = template.completedResponses || template.totalResponses || 0;
  const assignedCount = template.totalAssigned || 0;

  return (
    <div className="survey-results-page min-vh-100">
      <PageTitle title="Survey Results" description="View survey results and response analytics" />
      <Navbar />
      
       <div className="bg-white border-bottom">
        <Container className="pt-4">
        <div className="survey-results-page-header">
          <div>
            <div className="smaller text-muted text-uppercase">
              Evaluation Results · {template.totalResponses || 0} responses
            </div>
            <h2 className="mb-0 fw-bold">{template.title} Results</h2>
            {template.description?.trim() ? (
              <p className="survey-results-subtitle">{template.description}</p>
            ) : (
              <p className="text-muted small mb-0">
                View aggregated responses and performance metrics for this evaluation.
              </p>
            )}
          </div>
          <Button
            variant="light"
            size='sm'
            onClick={() => navigate('/evaluations')}
          >
            <i className="bi bi-arrow-left me-1" />
            Back to Dashboard
          </Button>
        </div>

        <Nav variant="pills-alt" activeKey={activeTab} onSelect={key => setActiveTab(key)}>
          <Nav.Item>
            <Nav.Link eventKey="overview">
              Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="questions">
              Questions
            </Nav.Link>
          </Nav.Item>
        </Nav>
   
        </Container>
        </div>
      <Container className="py-4 pt-0">
        {activeTab === 'overview' && (
          <div className="survey-results-overview mt-4">
            <Row>
              <Col lg={3} md={6} className='mb-3'>
                <MetricCard
                icon="bi-graph-up-arrow"
                iconClass="green"
                label="Completion Rate"
                value={`${metrics.completionRate}%`}
                subtext={parseFloat(metrics.completionRate) >= 100 ? 'Above target' : 'In progress'}
              />
              </Col>
              <Col lg={3} md={6} className='mb-3'>
              <MetricCard
                icon="bi-award"
                iconClass="orange"
                label="Average Score"
                value={metrics.averageScore}
                subtext="Out of 5.0"
              />
              </Col>
              <Col lg={3} md={6} className='mb-3'>
              <MetricCard
                icon="bi-people"
                iconClass="blue"
                label="Response Rate"
                value={`${metrics.responseRate}%`}
                subtext={`${answeredCount} of ${assignedCount} submitted`}
              />
              </Col>
              <Col lg={3} md={6}className='mb-3'>
              <MetricCard
                icon="bi-layers"
                iconClass="purple"
                label="Questions"
                value={questionCount}
                subtext="All answered"
              />
              </Col>
              </Row>
            

            <Card className="mb-4 bg-dark border-0">
              <Card.Body className=' p-4 d-flex flex-row justify-content-between just' >
                <div className=''>
                <Card.Title className="text-uppercase smaller text-white mb-0">
                  Overall Assessment
                </Card.Title>
                <Card.Text className="text-white mb-0">
                  {getAssessmentText(metrics, metrics.averageScore)}
                </Card.Text>
                </div>

                <Button
                  variant="light-solid"
                  onClick={() => setActiveTab('questions')}
                >
                  View all questions →
                </Button>
              </Card.Body>
            </Card>
       
          </div>
        )}
        <div className="mt-4">
        {activeTab === 'questions' && renderQuestionCards()}
        </div>
      </Container>
    </div>
  );
};

export default SurveyResults;
